import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/lib/avocado";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ForgotPasswordDialog } from "@/pages/ResetPassword";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success(`Welcome back, ${r.user.name.split(" ")[0]} 👋`);
      navigate(r.user.role === "admin" ? "/admin" : "/dashboard");
    } else {
      toast.error(r.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "#FFFCF7" }}>
      {/* Left brand panel — full hot pink */}
      <div className="relative hidden md:flex flex-col justify-between p-12" style={{ background: BRAND.pink }}>
        <div className="bg-dots absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.14) 1.5px, transparent 1.5px)", backgroundSize: "22px 22px" }} />
        <div className="relative z-10">
          <div className="uppercase-label whitespace-nowrap" style={{ color: "#FFFFFF", fontSize: "0.85rem", letterSpacing: "0.22em", fontWeight: 800 }}>
            Tom Gilbert <span style={{ color: BRAND.green, margin: "0 0.4em" }}>|</span> Avocado Property
          </div>
        </div>
        <div className="relative z-10 max-w-lg">
          <img
            src={BRAND.logoUrl}
            alt=""
            className="w-40 mb-8 rounded-2xl"
            style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.15)" }}
          />
          <p className="uppercase-label" style={{ color: "rgba(255,255,255,0.85)" }}>My Referral Community</p>
          <h1 className="font-display text-6xl lg:text-7xl mt-3 leading-[0.95] text-white italic" style={{ fontWeight: 700, color: BRAND.green }}>
            A proper<br/>thank-you.
          </h1>
          <p className="mt-6 text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.92)" }}>
            Send us a mate, a neighbour, a colleague – anyone thinking of selling. Tom looks after the property, you get <strong className="font-bold">5% of his fee</strong> as a thank-you.
          </p>
        </div>
        <div className="relative z-10 text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>
          Tom Gilbert · avocado <em>property</em> partner · <span className="brand-underline" style={{ background: "linear-gradient(transparent 70%, rgba(180,211,56,0.6) 70%)", padding: "0 0.15em" }}>New Model Agency of the Year 2023, 2024, 2025</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8"><BrandLogo /></div>
          <p className="uppercase-label" style={{ color: BRAND.pink }}>Sign in</p>
          <h2 className="font-display text-4xl md:text-5xl mt-1 mb-2" style={{ fontWeight: 700 }}>
            Hello <span className="italic">again.</span>
          </h2>
          <p className="text-sm mb-8" style={{ color: "#7A6D5F" }}>Right, let's have a look at your referrals.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="uppercase-label">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="rounded-xl h-12 bg-white border-2"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="uppercase-label">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-xl h-12 bg-white border-2"
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit" disabled={busy}
              className="btn-brand w-full h-12 rounded-full font-bold text-base flex items-center justify-center disabled:opacity-70"
              data-testid="login-submit-button"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </button>
          </form>

          <p className="text-sm mt-6" style={{ color: "#7A6D5F" }}>
            <button type="button" onClick={() => setForgotOpen(true)}
              className="font-bold underline underline-offset-4" style={{ color: BRAND.pink }} data-testid="login-forgot-link">
              Forgot email or password?
            </button>
          </p>
          <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
          <p className="text-sm mt-3" style={{ color: "#7A6D5F" }}>
            New to the community?{" "}
            <Link to="/register" className="font-bold underline underline-offset-4" style={{ color: BRAND.pink }} data-testid="login-to-register-link">
              Create a referrer account
            </Link>
          </p>
          <p className="text-xs mt-8" style={{ color: "#9CA396" }}>
            Tom Gilbert · <a href="https://avocadopropertyagents.co.uk/agent-profile/tom-gilbert" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground" data-testid="tom-profile-link">visit my Avocado Property page →</a>
          </p>
        </div>
      </div>
    </div>
  );
}
